<template>
    <svg
        aria-hidden="true"
        class="ai-draft-loading-border absolute inset-0 h-full w-full"
        :data-testid="`${testidPrefix}-border`"
        ref="perimeterLoader"
    >
        <defs>
            <linearGradient :id="gradientAId" x1="0%" x2="100%" y1="0%" y2="100%">
                <stop offset="0%" stop-color="#0000FF">
                    <animate
                        attributeName="offset"
                        dur="1.6s"
                        repeatCount="indefinite"
                        values="0%;8%;0%"
                    />
                </stop>
                <stop offset="34%" stop-color="#2D99FF">
                    <animate
                        attributeName="offset"
                        dur="1.6s"
                        repeatCount="indefinite"
                        values="28%;42%;28%"
                    />
                </stop>
                <stop offset="66%" stop-color="#EC4899">
                    <animate
                        attributeName="offset"
                        dur="1.6s"
                        repeatCount="indefinite"
                        values="58%;76%;58%"
                    />
                </stop>
                <stop offset="100%" stop-color="#FE3031">
                    <animate
                        attributeName="offset"
                        dur="1.6s"
                        repeatCount="indefinite"
                        values="100%;92%;100%"
                    />
                </stop>
                <animate
                    attributeName="x1"
                    dur="1.8s"
                    repeatCount="indefinite"
                    values="-35%;0%;35%;-35%"
                />
                <animate
                    attributeName="x2"
                    dur="1.8s"
                    repeatCount="indefinite"
                    values="65%;100%;135%;65%"
                />
                <animate
                    attributeName="y1"
                    dur="2.4s"
                    repeatCount="indefinite"
                    values="0%;35%;0%;0%"
                />
                <animate
                    attributeName="y2"
                    dur="2.4s"
                    repeatCount="indefinite"
                    values="100%;65%;100%;100%"
                />
            </linearGradient>
            <linearGradient :id="gradientBId" x1="100%" x2="0%" y1="100%" y2="0%">
                <stop offset="0%" stop-color="#0000FF">
                    <animate
                        attributeName="offset"
                        dur="1.6s"
                        repeatCount="indefinite"
                        values="0%;8%;0%"
                    />
                </stop>
                <stop offset="34%" stop-color="#2D99FF">
                    <animate
                        attributeName="offset"
                        dur="1.6s"
                        repeatCount="indefinite"
                        values="28%;42%;28%"
                    />
                </stop>
                <stop offset="66%" stop-color="#EC4899">
                    <animate
                        attributeName="offset"
                        dur="1.6s"
                        repeatCount="indefinite"
                        values="58%;76%;58%"
                    />
                </stop>
                <stop offset="100%" stop-color="#FE3031">
                    <animate
                        attributeName="offset"
                        dur="1.6s"
                        repeatCount="indefinite"
                        values="100%;92%;100%"
                    />
                </stop>
                <animate
                    attributeName="x1"
                    dur="1.8s"
                    repeatCount="indefinite"
                    values="135%;100%;65%;135%"
                />
                <animate
                    attributeName="x2"
                    dur="1.8s"
                    repeatCount="indefinite"
                    values="35%;0%;-35%;35%"
                />
                <animate
                    attributeName="y1"
                    dur="2.4s"
                    repeatCount="indefinite"
                    values="100%;65%;100%;100%"
                />
                <animate
                    attributeName="y2"
                    dur="2.4s"
                    repeatCount="indefinite"
                    values="0%;35%;0%;0%"
                />
            </linearGradient>
        </defs>
        <rect
            class="ai-draft-loading-perimeter-line ai-draft-loading-perimeter-line-a"
            :data-testid="`${testidPrefix}-border-line-a`"
            height="1"
            pathLength="100"
            rx="8"
            ry="8"
            :stroke="`url(#${gradientAId})`"
            width="1"
            x="3"
            y="3"
        />
        <rect
            class="ai-draft-loading-perimeter-line ai-draft-loading-perimeter-line-b"
            :data-testid="`${testidPrefix}-border-line-b`"
            height="1"
            pathLength="100"
            rx="8"
            ry="8"
            :stroke="`url(#${gradientBId})`"
            width="1"
            x="3"
            y="3"
        />
    </svg>
</template>

<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

    const props = defineProps<{
        testidPrefix: string;
    }>();

    // Fixed gradient ids: only one AI draft overlay (email XOR sms) mounts
    // at a time. Namespace by testidPrefix if two ever render together.
    const gradientAId = computed(() => `${props.testidPrefix}-gradient-a`);
    const gradientBId = computed(() => `${props.testidPrefix}-gradient-b`);

    const perimeterLoader = ref<SVGSVGElement | null>(null);
    let resizeObserver: ResizeObserver | null = null;

    const syncPerimeterLoader = () => {
        const svg = perimeterLoader.value;

        if (!svg) {
            return;
        }

        const bounds = svg.getBoundingClientRect();
        const width = Math.max(1, bounds.width);
        const height = Math.max(1, bounds.height);

        svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
        svg.querySelectorAll<SVGRectElement>('.ai-draft-loading-perimeter-line').forEach((line) => {
            line.setAttribute('width', String(Math.max(1, width - 6)));
            line.setAttribute('height', String(Math.max(1, height - 6)));
        });
    };

    onMounted(() => {
        syncPerimeterLoader();

        if (typeof ResizeObserver === 'undefined' || !perimeterLoader.value) {
            return;
        }

        resizeObserver = new ResizeObserver(syncPerimeterLoader);
        resizeObserver.observe(perimeterLoader.value);
    });

    onBeforeUnmount(() => {
        resizeObserver?.disconnect();
    });
</script>

<style scoped>
    .ai-draft-loading-border {
        pointer-events: none;
    }

    .ai-draft-loading-perimeter-line {
        fill: none;
        filter: drop-shadow(0 0 1px rgb(236 72 153 / 14%));
        opacity: 0.98;
        stroke-dasharray: 21 79;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2px;
        vector-effect: non-scaling-stroke;
    }

    .ai-draft-loading-perimeter-line-a {
        animation: ai-draft-perimeter-dash-a 6.4s linear infinite;
        stroke-dashoffset: 0;
    }

    .ai-draft-loading-perimeter-line-b {
        animation: ai-draft-perimeter-dash-b 6.4s linear infinite;
        stroke-dashoffset: -50;
    }

    @keyframes ai-draft-perimeter-dash-a {
        from {
            stroke-dashoffset: 0;
        }

        to {
            stroke-dashoffset: -100;
        }
    }

    @keyframes ai-draft-perimeter-dash-b {
        from {
            stroke-dashoffset: -50;
        }

        to {
            stroke-dashoffset: -150;
        }
    }
</style>
